<template>
  <div class="min-h-screen flex flex-col">
    <main class="flex-1">
      <div class="w-full px-0 py-2">
        <div class="header justify-between" :class="{ 'tv-mode': isTV }">
          <!-- Logo Section -->
          <div class="logo">
            <svg
                class="icon-chart"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M3 3v17a1 1 0 001 1h17v-2H5V3H3z" />
              <path d="M15 10v8h2v-8h-2zM11 6v12h2V6h-2zM7 14v4h2v-4H7zM19 4v14h2V4h-2z" />
            </svg>
            <span class="app-title">Lunchlytics</span>
          </div>

          <!-- Navigation Tabs -->
          <nav v-if="!isTV" class="tabs">
            <router-link to="/dashboard" class="tab" active-class="tab-active">
              <svg class="tab-icon" viewBox="0 0 24 24">
                <path d="M3 3v17h17M9 17V9m4 8V5m4 12v-6" />
              </svg>
              <span class="tab-label">Analytics</span>
            </router-link>

            <router-link to="/history" class="tab" active-class="tab-active">
              <svg class="tab-icon" viewBox="0 0 24 24">
                <path d="M12 8v5l4 2" />
                <path d="M12 2a10 10 0 1 0 10 10" />
                <path d="M22 4v6h-6" />
              </svg>
              <span class="tab-label">History</span>
            </router-link>

            <router-link to="/info" class="tab" active-class="tab-active">
              <svg class="tab-icon" viewBox="0 0 24 24">
                <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm.75 15h-1.5v-6h1.5v6zm0-8h-1.5V7h1.5v2z" />
              </svg>
              <span class="tab-label">Info</span>
            </router-link>

            <router-link to="/stats" class="tab" active-class="tab-active">
              <svg class="tab-icon" viewBox="0 0 24 24">
                <path d="M3 12h4l2-5 4 10 2-5h6" />
              </svg>
              <span class="tab-label">Status</span>
            </router-link>

            <router-link to="/download" v-if="computedDefferedPrompt" class="tab" active-class="tab-active">
              <svg class="tab-icon" viewBox="0 0 24 24">
                <path d="M12 16l4-5h-3V4h-2v7H8l4 5zm-8 2h16v2H4v-2z" />
              </svg>
              <span class="tab-label">Download</span>
            </router-link>

            <router-link to="/impressum" class="tab" active-class="tab-active">
              <svg class="tab-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M24.707,8.793l-6.5-6.5C18.019,2.105,17.765,2,17.5,2H7C5.895,2,5,2.895,5,4v22c0,1.105,0.895,2,2,2h16c1.105,0,2-0.895,2-2 V9.5C25,9.235,24.895,8.981,24.707,8.793z M18,21h-8c-0.552,0-1-0.448-1-1c0-0.552,0.448-1,1-1h8c0.552,0,1,0.448,1,1 C19,20.552,18.552,21,18,21z M20,17H10c-0.552,0-1-0.448-1-1c0-0.552,0.448-1,1-1h10c0.552,0,1,0.448,1,1C21,16.552,20.552,17,20,17 z M18,10c-0.552,0-1-0.448-1-1V3.904L23.096,10H18z"></path>
              </svg>
              <span class="tab-label">Impressum</span>
            </router-link>
          </nav>
        </div>

        <!-- Routed content -->
        <router-view />

        <!-- Consent banner -->
        <ConsentBanner />
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import {defineComponent, onMounted, ref} from 'vue'
import ConsentBanner from './components/ConsentBanner.vue'

import { deferredPrompt } from './stores/deferredPromptStore.ts'


export default defineComponent({
  name: 'App',
  components: { ConsentBanner },
  setup() {
    const isTV = ref(false)

    onMounted(() => {
      const params = new URLSearchParams(window.location.search)
      isTV.value = params.get('mode') === 'tv'
    })

    return { isTV, deferredPrompt }
  },
  computed: {
    computedDefferedPrompt() {
      return deferredPrompt.value
    }
  }
})
</script>

<style>
/* General styles */
html, body {
  overflow-x: hidden;
  max-width: 100%;
  margin: 0;
  padding: 0;
  background-color: #121212;
  color: #ddd;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

@media (max-width: 640px) {
  html {
    font-size: 14px;
  }
}

/* Header layout */
.header {
  background-color: #1e1e1e;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  z-index: 10;
}

/* Logo styling */
.logo {
  display: flex;
  align-items: center;
}

.app-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #8dbc05;
  margin-left: 10px;
}

.icon-chart {
  width: 24px;
  height: 24px;
  fill: #8dbc05;
}

/* Tab navigation styles */
.tabs {
  display: flex;
  gap: 10px;
  flex-wrap: nowrap;
  overflow-x: auto;
  max-width: 100%;
}

.tab {
  padding: 6px 12px;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 500;
  color: #a0aec0;
  transition: background-color 0.2s;
}

.tab:hover {
  background-color: #2a2a2a;
}

.tab-active {
  background-color: #8dbc05;
  color: #121212;
}

.tab-active:hover {
  background-color: #81a905;
  color: #121212;
}

.tab-icon {
  width: 20px;
  height: 20px;
  min-width: 20px;
  margin-top: -3px; /*cheating the alignment*/
  margin-right: 3px;
  min-height: 20px;
  display: inline-block;
  vertical-align: middle;
  stroke-width: 1.5;
  stroke: currentColor;
  fill: none;
}

.tab-label {
  font-size: 1rem;
}

@media (max-width: 768px) {
  .tab-label {
    display: none;
  }

  .tab {
    padding: 4px 6px;
  }

  .tabs {
    gap: 4px;
  }
}


.header.tv-mode {
  background: none;
  box-shadow: none;
  justify-content: center;
  padding: 1rem 0 0;
}

.header.tv-mode .icon-chart {
  width: 3.5rem;
  height: 3.5rem;
  transform: scale(1.2);
}

.header.tv-mode .app-title {
  font-size: 3.5rem;
  font-weight: 700;
  margin-left: 1.5rem;
  color: #8dbc05;
  text-shadow: 0 0 10px rgba(141, 188, 5, 0.4);
}

</style>
